import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Surface } from '@oxyflows/domain';
import { api, type ChartData } from '../api';
import { useI18n } from '../i18n';
import { ErrorState, Field, Modal, Skeleton, Spinner } from '../components';
import { formatMinor, minorToInput, parseToMinor } from '../money';
import { Odontogram } from './Odontogram';
import { ImageViewer, type ViewerImage } from './ImageViewer';
import { SensorCapture } from './SensorCapture';
import { isDesktop } from './DesktopBar';
import { describe } from '../errors';

/**
 * The charting workspace for ONE patient (spec §6, §7).
 *
 * It used to be a top-level section with its own patient picker, which meant
 * choosing a patient twice and reading their allergies somewhere else. A chart
 * belongs to a mouth and a mouth belongs to a person, so it now lives inside
 * the patient record, under the same medical alerts, and takes the patient it
 * is charting as a prop.
 *
 * Select a tooth, optionally select surfaces, then record a finding, a
 * treatment, a note or an image. The tooth's detail panel shows its own media
 * gallery — the same stored files as the patient's imaging library, never a
 * second copy.
 */
export function ChartPanel({
  patientId,
  birthDate,
  canWrite,
}: {
  patientId: string;
  birthDate: string | null;
  canWrite: boolean;
}) {
  const { t, locale, formatDate } = useI18n();
  const [chart, setChart] = useState<ChartData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedTooth, setSelectedTooth] = useState<string | null>(null);
  const [selectedSurfaces, setSelectedSurfaces] = useState<Surface[]>([]);
  const [viewing, setViewing] = useState<number | null>(null);
  const [dentition, setDentition] = useState<'permanent' | 'primary' | 'mixed'>(() => suggestDentition(birthDate));
  const [dialog, setDialog] = useState<'finding' | 'surface' | 'treatment' | 'note' | 'capture' | null>(null);
  /** On the Windows application the capture goes to the real sensor (or the labelled simulator). */
  const [sensor, setSensor] = useState(false);
  const [nonce, setNonce] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await api.chart(patientId);
      setChart(data);
      // Suggest a chart from age, but never impose one: the clinician confirms
      // what is actually erupted (spec §6).
      setDentition(suggestDentition(data.patient.birthDate));
    } catch (e) {
      setError(describe(e, t));
    }
  }, [patientId, t]);

  /*
    Two different reasons to reload, and they must not be treated the same.

    A NEW PATIENT means nothing selected — the tooth you were looking at
    belongs to someone else. But saving a procedure also reloads the chart,
    and clearing the selection then threw the clinician back to the empty
    "select a tooth" state with the dialog they had just used gone: the
    procedure was saved and the screen behaved as if it had not been. After a
    save the tooth stays selected, the chart underneath it updates, and only
    the surface selection resets, because the surfaces were the input to what
    was just recorded.
  */
  useEffect(() => {
    setChart(null);
    setSelectedTooth(null);
    setSelectedSurfaces([]);
    void load();
  }, [load]);

  useEffect(() => {
    if (nonce === 0) return;
    setSelectedSurfaces([]);
    void load();
  }, [load, nonce]);

  const imageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const img of chart?.images ?? []) {
      if (img.toothFdi) counts[img.toothFdi] = (counts[img.toothFdi] ?? 0) + 1;
    }
    return counts;
  }, [chart]);

  const toothImages = useMemo(
    () => (chart?.images ?? []).filter((i) => i.toothFdi === selectedTooth),
    [chart, selectedTooth],
  );

  const viewerImages: ViewerImage[] = toothImages.map((img) => ({ id: img.attachmentId, kind: img.kind, at: String(img.at), isSynthetic: img.isSynthetic, toothFdi: img.toothFdi }));

  const toothTreatments = useMemo(
    () => (chart?.treatments ?? []).filter((x) => x.toothFdi === selectedTooth),
    [chart, selectedTooth],
  );

  const toothState = chart?.teeth.find((x) => x.toothFdi === selectedTooth);
  const toothSurfaces = (chart?.surfaces ?? []).filter((s) => s.toothFdi === selectedTooth);

  const toggleSurface = (s: Surface) =>
    setSelectedSurfaces((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  if (error && !chart) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  if (!chart) {
    return (
      <div className="card">
        <Skeleton rows={8} />
      </div>
    );
  }

  return (
    <>
      <div className="chart-layout">
        <div className="card">
          <div className="card-head">
            <div>
              <h2>{t.chart.title}</h2>
              <div className="page-sub">{t.chart.subtitle}</div>
            </div>
            <div className="segmented" role="group" aria-label={t.chart.dentition}>
              {(['permanent', 'mixed', 'primary'] as const).map((d) => (
                <button key={d} type="button" onClick={() => setDentition(d)} aria-pressed={d === dentition}>
                  {(t.chart.dentitionKind as Record<string, string>)[d]}
                </button>
              ))}
            </div>
          </div>
          <div className="card-body">
            <Odontogram
              dentition={dentition}
              teeth={chart.teeth}
              surfaces={chart.surfaces}
              imageCounts={imageCounts}
              selectedTooth={selectedTooth}
              selectedSurfaces={selectedSurfaces}
              onSelectTooth={(fdi) => {
                setSelectedTooth(fdi);
                setSelectedSurfaces([]);
              }}
              onToggleSurface={toggleSurface}
            />
          </div>
        </div>

        {/* The per-tooth detail panel required by spec §6. */}
        <div className="card">
          <div className="card-head">
            <h2>{selectedTooth ? `${t.chart.tooth} ${selectedTooth}` : t.chart.selectTooth}</h2>
            {selectedSurfaces.length > 0 ? (
              <span className="badge brand">
                {selectedSurfaces.length} {t.chart.surfacesSelected}
              </span>
            ) : null}
          </div>

          {!selectedTooth ? (
            <div className="empty">
              <div className="empty-title">{t.chart.selectTooth}</div>
              <div>{t.chart.selectToothHint}</div>
            </div>
          ) : (
            <div className="card-body stack" style={{ gap: 16 }}>
              <div>
                <div className="section-label">{t.chart.currentStatus}</div>
                <div className="row" style={{ marginTop: 6, flexWrap: 'wrap' }}>
                  <span className="badge">
                    {(t.chart.status as Record<string, string>)[toothState?.status ?? 'sound'] ??
                      toothState?.status ??
                      t.chart.status.sound}
                  </span>
                  {toothSurfaces.map((s) => (
                    <span key={s.surface} className="badge info">
                      {(t.chart.surface as Record<string, string>)[s.surface] ?? s.surface}:{' '}
                      {(t.chart.finding as Record<string, string>)[s.finding] ?? s.finding}
                    </span>
                  ))}
                </div>
                {toothState?.note ? (
                  <p style={{ color: 'var(--ink-2)', marginTop: 8, marginBottom: 0 }}>{toothState.note}</p>
                ) : null}
              </div>

              {canWrite ? (
                <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
                  <button type="button" className="btn btn-sm" onClick={() => setDialog('finding')}>
                    {t.chart.recordFinding}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setDialog('surface')}
                    disabled={selectedSurfaces.length === 0}
                    title={selectedSurfaces.length === 0 ? t.chart.selectSurfacesFirst : undefined}
                  >
                    {t.chart.recordSurface}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDialog('treatment')}>
                    {t.chart.addTreatment}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDialog('note')}>
                    {t.chart.addNote}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => (isDesktop() ? setSensor(true) : setDialog('capture'))}>
                    {t.chart.capture}
                  </button>
                </div>
              ) : null}
              {sensor && selectedTooth ? (
                <SensorCapture
                  patientId={patientId}
                  defaultToothFdi={selectedTooth}
                  onClose={() => setSensor(false)}
                  onSaved={() => {
                    setSensor(false);
                    void load();
                  }}
                />
              ) : null}

              <div>
                <div className="section-label">{t.chart.media}</div>
                {toothImages.length === 0 ? (
                  <p style={{ color: 'var(--ink-3)', marginTop: 6 }}>{t.chart.noMedia}</p>
                ) : (
                  <div className="thumb-grid">
                    {toothImages.map((img, i) => (
                      <button key={img.linkId} type="button" className="thumb thumb-btn" onClick={() => setViewing(i)}>
                        <img
                          src={`/api/imaging/file/${img.attachmentId}`}
                          alt={`${img.kind} for tooth ${img.toothFdi}`}
                          loading="lazy"
                        />
                        {/* A simulated capture is labelled everywhere it
                            appears — it can never pass for a real image. */}
                        {img.isSynthetic ? <span className="thumb-tag warn">{t.chart.simulated}</span> : null}
                        <span className="thumb-meta">{formatDate(String(img.at))}</span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 6 }}>
                  {t.chart.sharedFileNote}
                </p>
              </div>

              <div>
                <div className="section-label">{t.chart.treatments}</div>
                {toothTreatments.length === 0 ? (
                  <p style={{ color: 'var(--ink-3)', marginTop: 6 }}>{t.chart.noTreatments}</p>
                ) : (
                  <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
                    {toothTreatments.map((x) => (
                      <li key={x.id}>
                        {locale === 'ar' ? x.nameAr : x.nameEn}
                        {x.surfaces.length > 0 ? ` (${x.surfaces.map((s) => (t.chart.surface as Record<string, string>)[s] ?? s).join('/')})` : ''} ·{' '}
                        <span className="badge">
                          {(t.treatmentStatus as Record<string, string>)[x.status] ?? x.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {dialog && selectedTooth ? (
        <ChartDialog
          kind={dialog}
          patientId={patientId}
          toothFdi={selectedTooth}
          surfaces={selectedSurfaces}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
      {viewing !== null && viewerImages[viewing] ? (
        <ImageViewer images={viewerImages} index={viewing} onClose={() => setViewing(null)} onIndexChange={setViewing} canAnnotate={canWrite} />
      ) : null}
    </>
  );
}

/** Age only SUGGESTS a chart. The clinician confirms what is actually erupted. */
function suggestDentition(birthDate: string | null): 'permanent' | 'primary' | 'mixed' {
  if (!birthDate) return 'permanent';
  const age = Math.floor((Date.now() - new Date(birthDate).getTime()) / (365.25 * 86_400_000));
  return age < 6 ? 'primary' : age < 13 ? 'mixed' : 'permanent';
}

function ChartDialog({
  kind,
  patientId,
  toothFdi,
  surfaces,
  onClose,
  onDone,
}: {
  kind: 'finding' | 'surface' | 'treatment' | 'note' | 'capture';
  patientId: string;
  toothFdi: string;
  surfaces: Surface[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, tx, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('caries');
  const [finding, setFinding] = useState('caries');
  const [note, setNote] = useState('');
  const [catalogue, setCatalogue] = useState<Awaited<ReturnType<typeof api.catalogue>>['items']>([]);
  const [catalogueId, setCatalogueId] = useState('');
  const [price, setPrice] = useState('');
  const [treatmentStatus, setTreatmentStatus] = useState<'planned' | 'completed'>('planned');
  // Whether the patient is charged for this. Almost always yes; the exceptions
  // are warranty redos, courtesies and history entered for the record. It was
  // stored but never shown, which made an unbillable treatment impossible to
  // explain or undo from the screen.
  const [billable, setBillable] = useState(true);
  const [performedOn, setPerformedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [confirmPatient, setConfirmPatient] = useState(false);

  const chosen = catalogue.find((c) => c.id === catalogueId) ?? null;

  useEffect(() => {
    if (kind !== 'treatment') return;
    void api
      .catalogue()
      .then((r) => {
        // The dental chart offers dental work (and a clinic's general items);
        // what belongs on the face and body chart is offered there.
        const items = r.items.filter((c) => (c.specialty === 'dental' || c.specialty === 'general') && !c.requiresZone);
        setCatalogue(items);
        if (items[0]) {
          setCatalogueId(items[0].id);
          setPrice(minorToInput(items[0].priceMinor, items[0].currency));
        }
      })
      .catch(() => setError(t.common.error));
  }, [kind, t]);

  const titles: Record<typeof kind, string> = {
    finding: t.chart.recordFinding,
    surface: t.chart.recordSurface,
    treatment: t.chart.addTreatment,
    note: t.chart.addNote,
    capture: t.chart.capture,
  };

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (kind === 'finding') {
        await api.recordObservation(patientId, { toothFdi, status, note: note || undefined });
      } else if (kind === 'surface') {
        await api.recordSurfaces(patientId, { toothFdi, surfaces, finding, note: note || undefined });
      } else if (kind === 'treatment') {
        const item = catalogue.find((c) => c.id === catalogueId);
        if (!item) throw new Error('no item');
        const priceMinor = parseToMinor(price, item.currency);
        if (priceMinor === null) {
          setError(t.chart.priceInvalid);
          return;
        }
        await api.createTreatment({
          patientId,
          catalogueId: item.id,
          nameEn: item.nameEn,
          nameAr: item.nameAr,
          toothFdi: item.requiresTooth ? toothFdi : null,
          surfaces: item.requiresTooth ? surfaces : [],
          // The price AS CHARGED for this patient, which is not always the
          // catalogue price — the same procedure is discounted, quoted at a
          // family rate, or adjusted. The code stays the code.
          priceMinor,
          currency: item.currency,
          status: treatmentStatus,
          billable,
          performedAt: treatmentStatus === 'completed' ? `${performedOn}T12:00:00.000Z` : undefined,
          notes: note || undefined,
        });
      } else if (kind === 'note') {
        await api.createNote({ patientId, body: note, toothFdi, finalise: true });
      } else if (kind === 'capture') {
        await api.simulateCapture({ patientId, toothFdi, surfaces, patientConfirmed: true });
      }
      onDone();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${titles[kind]} — ${t.chart.tooth} ${toothFdi}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void submit()}
            disabled={busy || (kind === 'note' && note.trim().length === 0) || (kind === 'capture' && !confirmPatient)}
          >
            {busy ? (
              <>
                <Spinner /> {t.patients.saving}
              </>
            ) : (
              t.chart.save
            )}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        {surfaces.length > 0 ? (
          <div className="notice">
            <span>
              {t.chart.surfacesSelected}: <strong>{surfaces.map((s) => (t.chart.surface as Record<string, string>)[s] ?? s).join(locale === 'ar' ? '، ' : ', ')}</strong>
            </span>
          </div>
        ) : null}

        {kind === 'finding' ? (
          <Field label={t.chart.currentStatus} required>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {Object.keys(t.chart.status).map((k) => (
                <option key={k} value={k}>
                  {(t.chart.status as Record<string, string>)[k]}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {kind === 'surface' ? (
          <Field label={t.chart.recordSurface} required>
            <select value={finding} onChange={(e) => setFinding(e.target.value)}>
              {Object.keys(t.chart.finding).map((k) => (
                <option key={k} value={k}>
                  {(t.chart.finding as Record<string, string>)[k]}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {kind === 'treatment' ? (
          <>
            <Field label={t.chart.procedure} required>
              <select
                value={catalogueId}
                onChange={(e) => {
                  setCatalogueId(e.target.value);
                  // The catalogue price is the STARTING POINT, refilled when
                  // the procedure changes so nobody charges a crown at the
                  // price of the filling they were looking at a moment ago.
                  const next = catalogue.find((c) => c.id === e.target.value);
                  if (next) setPrice(minorToInput(next.priceMinor, next.currency));
                }}
              >
                {catalogue.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} · {locale === 'ar' ? c.nameAr : c.nameEn}
                  </option>
                ))}
              </select>
            </Field>

            <div className="grid-2">
              <Field
                label={`${t.chart.price}${chosen ? ` (${chosen.currency})` : ''}`}
                hint={
                  chosen && parseToMinor(price, chosen.currency) !== chosen.priceMinor
                    ? `${t.chart.cataloguePrice}: ${formatMinor(chosen.priceMinor, chosen.currency, locale)}`
                    : t.chart.priceHint
                }
                required
              >
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  inputMode="decimal"
                  className="ltr-value"
                />
              </Field>

              <Field label={t.common.status} required>
                <select
                  value={treatmentStatus}
                  onChange={(e) => setTreatmentStatus(e.target.value as 'planned' | 'completed')}
                >
                  <option value="planned">{t.treatmentStatus.planned}</option>
                  <option value="completed">{t.treatmentStatus.completed}</option>
                </select>
              </Field>
            </div>

            <label className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={billable}
                onChange={(e) => setBillable(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>{t.chart.chargeable}</strong>
                <span className="sub" style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>
                  {t.chart.chargeableHint}
                </span>
              </span>
            </label>

            {treatmentStatus === 'completed' ? (
              <Field label={t.chart.performedOn} hint={t.chart.performedOnHint}>
                <input
                  type="date"
                  value={performedOn}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setPerformedOn(e.target.value)}
                  className="ltr-value"
                />
              </Field>
            ) : null}

            {/*
              Said BEFORE it happens, not discovered afterwards: completing
              this marks the tooth on the chart, and a dentist should know that
              they are about to change the drawing.
            */}
            {treatmentStatus === 'completed' && chosen?.resultingToothStatus && chosen.requiresTooth ? (
              <div className="notice">
                <span>
                  {tx(t.chart.willChart, {
                    tooth: toothFdi,
                    status:
                      (t.chart.status as Record<string, string>)[chosen.resultingToothStatus] ??
                      chosen.resultingToothStatus,
                  })}
                </span>
              </div>
            ) : null}
          </>
        ) : null}

        {kind === 'capture' ? (
          <>
            {/* The simulator is labelled before anything is captured, not
                after (spec §7, T10). */}
            <div className="notice warn">
              <span>{t.chart.simulatorWarning}</span>
            </div>
            <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={confirmPatient}
                onChange={(e) => setConfirmPatient(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>{t.chart.confirmPatient}</span>
            </label>
          </>
        ) : null}

        {kind !== 'capture' ? (
          <Field label={kind === 'note' ? t.chart.addNote : t.patients.notes} required={kind === 'note'}>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={kind === 'note' ? 5 : 3} />
          </Field>
        ) : null}
      </div>
    </Modal>
  );
}

